import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { AutoScrollActivator, closestCorners, DndContext, DragEndEvent, TraversalOrder } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRelayStore } from 'src/relay';
import { getPathName, toTitleCase } from 'src/shared/helpers';
import DragIndicatorIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import StarIcon from 'src/shared/material_icons/toggle/star.svg';
import StarBorderIcon from 'src/shared/material_icons/toggle/star_border.svg';
import { usePageTransition } from 'src/shared/navigationTransition';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { Icon } from '../icon';
import { AiTag, ComingSoonTag, NewTag } from '../tag';
import { ZTooltip } from '../ztooltip';
import { isMenuItemSelected, isMenuKeySelected, isSubMenuItemSelected, T } from './helpers';
import { GetIcon, GetMenuItemIcon } from './IconManager';
import {
    ContainerArrow,
    ContainerIcon,
    LinkSidebar,
    MenuDivider,
    MenuSectionArrow,
    MenuSectionBody,
    MenuSectionHeader,
    MenuSectionIcon,
    Navbar,
    NavbarContainer,
    NavbarLi,
    StarredDragHandle,
    StarredSortableItemContainer,
    StarToggle
} from './leftAppSidebarStyles';
import type { SidebarMenuItemsProps } from './leftAppSidebarTypes';
import { IsSubMenu, ItemMenu } from './Menu';
import { isStarExcludedKey, isStarredKeyMatch, useStarredMenu } from './StarredMenu';

export const SidebarSelectedMenuItem = styled.span`
    color: ${theme.app.secondary};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// The label uses the normal menu text color; only the icon carries the AI color.
const SidebarAiSelectedMenuItem = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SidebarMenuItemLabel = styled.span`
    display: flex;
    align-items: center;
    min-width: 0;
`;

// Items of a sub-category (e.g. Libraries inside Design Tools), indented under its header.
const SubCategoryItems = styled.div`
    padding-left: 12px;
`;

const SidebarMenuItemName = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const isAllowed = (iamUserPermissions: string[], iamUser: boolean, permissionsToHave: string[]) => {
    if (!iamUser || permissionsToHave.length === 0) {
        return true;
    }

    return permissionsToHave.every((p) => iamUserPermissions.includes(p));
};

const isVisibleMenuItem = (item: ItemMenu) => !item.isHidden;

const SidebarSubmenuList = (
    itemMenu: ItemMenu,
    goToMenu: (item: ItemMenu) => any,
    iamUser: boolean,
    iamUserPermissions: string[],
    bestSelected: ItemMenu | null
) => {
    return itemMenu.menu.filter(isVisibleMenuItem).map((item, i) => (
        <div key={i}>
            <SidebarMenuItem
                itemMenu={item}
                isHeader={false}
                goToMenu={goToMenu}
                disabled={!isAllowed(iamUserPermissions, iamUser, item.permissions)}
                bestSelected={bestSelected}
            />
        </div>
    ));
};

interface SidebarMenuItemProps {
    itemMenu: ItemMenu;
    isHeader: boolean;
    goToMenu: (item: ItemMenu) => any;
    disabled?: boolean;
    onBeforeNavigate?: () => void;
    bestSelected?: ItemMenu | null;
    hideIcon?: boolean;
    forceNavigate?: boolean;
    isProminent?: boolean;
    canStar?: boolean;
    canReorder?: boolean;
    starredKey?: string;
    dragAttributes?: DraggableAttributes;
    dragListeners?: DraggableSyntheticListeners;
    setDragHandleRef?: (element: HTMLElement | null) => void;
}

const SidebarMenuItem: FunctionComponent<SidebarMenuItemProps> = ({
    itemMenu,
    isHeader,
    goToMenu,
    onBeforeNavigate,
    bestSelected,
    hideIcon,
    forceNavigate,
    isProminent,
    canStar,
    canReorder,
    starredKey,
    dragAttributes,
    dragListeners,
    setDragHandleRef
}) => {
    const [isDirty, incrementStoreKey] = useRelayStore((state) => [state.isRelayStoreDirty, state.incrementRelayStoreKey]);
    const navigate = useNavigate();
    const { startPageTransition } = usePageTransition();
    const { isStarred, toggleStar, isSaving } = useStarredMenu();

    const intelNav = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        if (!forceNavigate && !isHeader && IsSubMenu(itemMenu) && e.currentTarget.target !== '_blank') {
            goToMenu(itemMenu);
            return;
        }

        onBeforeNavigate?.();

        // Emit custom event to allow other components to intercept navigation
        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: {
                path: getPathName(itemMenu.urlPage),
                urlPage: itemMenu.urlPage,
                isDam: window.location.href.toLowerCase().includes('dam')
            },
            cancelable: true
        });

        window.dispatchEvent(navigationEvent);

        // If the event was not prevented, proceed with navigation
        if (!navigationEvent.defaultPrevented) {
            if (isDirty) incrementStoreKey();
            // itemMenu.urlPage is absolute (it comes from the backend treeMenu); navigate via the
            // current origin's pathname, otherwise on localhost (or staging) we'd jump to the
            // production host embedded in the URL.
            startPageTransition(() => {
                navigate(getPathName(itemMenu.urlPage));
            });
        }
    };

    const handleAuxClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (e.button == 1) {
            // Middle click
            e.preventDefault();
            window.open(itemMenu.urlPage, '_blank');
            return;
        }
    };

    const isSelected = bestSelected !== undefined ? isMenuItemExactSelected(itemMenu, bestSelected) : isMenuItemSelected(itemMenu);
    const MenuIcon = GetMenuItemIcon(itemMenu);
    const showStar = canStar && !isHeader && isStarrableItem(itemMenu);
    const showDragHandle = canReorder && !!starredKey;
    const itemIsStarred = showStar && isStarred(itemMenu);
    return (
        <NavbarLi $isVisible={itemMenu.name.toLowerCase() !== 'dam'}>
            <LinkSidebar
                href={isHeader ? 'javascript:void(0)' : itemMenu.urlPage}
                $isHeader={isHeader}
                $actionsOffset={showDragHandle ? 52 : undefined}
                onClick={intelNav}
                onAuxClick={handleAuxClick}
                target={itemMenu.isExternalUrl ? '_blank' : '_self'}
            >
                {!hideIcon && <ContainerIcon $isSelected={isSelected && !itemMenu.isAI}>{MenuIcon && React.createElement(MenuIcon)}</ContainerIcon>}

                <SidebarMenuItemLabel>
                    {isSelected ? (
                        itemMenu.isAI ? (
                            <SidebarAiSelectedMenuItem>{toTitleCase(itemMenu.name)}</SidebarAiSelectedMenuItem>
                        ) : (
                            <SidebarSelectedMenuItem>{toTitleCase(itemMenu.name)}</SidebarSelectedMenuItem>
                        )
                    ) : (
                        <SidebarMenuItemName>{toTitleCase(itemMenu.name)}</SidebarMenuItemName>
                    )}

                    {itemMenu.isAI && <AiTag>AI</AiTag>}
                    {itemMenu.isNew && !itemMenu.isAI && <NewTag>New</NewTag>}
                    {itemMenu.isComingSoon && <ComingSoonTag>{T._('Coming soon', 'Admin')}</ComingSoonTag>}
                </SidebarMenuItemLabel>
                <ContainerArrow $isHidden={IsSubMenu(itemMenu) && !isHeader}>
                    <Icon>{GetIcon('angle_right_solid') && React.createElement(GetIcon('angle_right_solid')!)}</Icon>
                </ContainerArrow>
            </LinkSidebar>
            {showDragHandle && (
                <StarredDragHandle
                    type='button'
                    className='sidebar-view-icon'
                    aria-label={T._('Reorder starred item', 'Admin')}
                    ref={setDragHandleRef}
                    {...dragAttributes}
                    {...dragListeners}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <Icon>
                        <DragIndicatorIcon />
                    </Icon>
                </StarredDragHandle>
            )}
            {showStar && (
                <ZTooltip position='top' title={itemIsStarred ? T._('Remove from starred', 'Admin') : T._('Add to starred', 'Admin')}>
                    <StarToggle
                        type='button'
                        className='sidebar-view-icon'
                        $active={itemIsStarred}
                        disabled={isSaving}
                        aria-label={itemIsStarred ? T._('Remove from starred', 'Admin') : T._('Add to starred', 'Admin')}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleStar(itemMenu);
                        }}
                    >
                        <Icon>{itemIsStarred ? <StarIcon /> : <StarBorderIcon />}</Icon>
                    </StarToggle>
                </ZTooltip>
            )}
        </NavbarLi>
    );
};

// ── Main menu categorization (HOME level) ───────────────────────────
// The server provides the intended top-level structure. The frontend only keeps
// presentation rules for Home items, collapsible categories, and starred pages.

const flattenMenuItems = (root: ItemMenu): ItemMenu[] => {
    const result: ItemMenu[] = [];
    const walk = (items: ItemMenu[]) => {
        for (const item of items) {
            if (!isVisibleMenuItem(item)) continue;
            result.push(item);
            if (item.menu && item.menu.length > 0) walk(item.menu);
        }
    };
    walk(root.menu || []);
    return result;
};

const normalizeMenuName = (name: string) =>
    name
        .trim()
        .toLowerCase()
        .replace(/[-_\s]+/g, ' ');

const isHomeMenuItem = (item: ItemMenu) => normalizeMenuName(item.name) === 'home';

const hasMenuUrl = (item: ItemMenu) => {
    const url = item.urlPage?.trim();
    return Boolean(url && !/^\d+$/.test(url));
};

// Items that never make sense as standalone starred entries.
const STARRED_EXCLUDED_NAMES = new Set(['home', 'dam']);

// A leaf page (no children) with a real URL that the user is allowed to star.
// Fixed menu entries (Customizable Products, Printing Methods, Orders) are
// excluded — they are always present in the menu, so starring them is redundant.
const isStarrableItem = (item: ItemMenu) =>
    !IsSubMenu(item) && hasMenuUrl(item) && !STARRED_EXCLUDED_NAMES.has(item.name.trim().toLowerCase()) && !isStarExcludedKey(item.key);

type MenuBlock =
    | { kind: 'home-item'; item: ItemMenu }
    | {
          kind: 'category';
          key: string;
          openKey: string;
          label: string;
          icon: string;
          items: ItemMenu[];
          headerItem?: ItemMenu;
      };

const findBestSelectedItem = (root: ItemMenu, isSelected: (item: ItemMenu) => boolean = isMenuItemSelected): ItemMenu | null => {
    let best: ItemMenu | null = null;
    const walk = (items: ItemMenu[]) => {
        for (const item of items) {
            if (!isVisibleMenuItem(item)) continue;
            if (isSelected(item)) {
                if (!best || item.urlPage.length > best.urlPage.length) {
                    best = item;
                }
            }
            if (item.menu.length > 0) walk(item.menu);
        }
    };
    walk(root.menu || []);
    return best;
};

const isMenuItemExactSelected = (item: ItemMenu, bestSelected: ItemMenu | null): boolean => {
    if (!bestSelected) return false;
    return (item.key && item.key === bestSelected.key) || item.urlPage === bestSelected.urlPage;
};

// ── Custom menu reorganization ──────────────────────────────────────
// Top-level sections are rendered in the server-provided order.

const getCategoryOpenKey = (item: ItemMenu) => item.key || item.urlPage || normalizeMenuName(item.name);

const getCategoryKey = (item: ItemMenu, index: number) => {
    const normalizedName = normalizeMenuName(item.name)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return item.key || item.urlPage || `${normalizedName || 'category'}-${index}`;
};

const buildCategorizedMenu = (root: ItemMenu): MenuBlock[] => {
    const blocks: MenuBlock[] = [];
    const skipHomeItems = ['dam'];

    // Home renders its children as normal top-level items, except DAM.
    for (const [index, item] of (root.menu || []).entries()) {
        if (!isVisibleMenuItem(item)) continue;

        if (isHomeMenuItem(item)) {
            (item.menu || [])
                .filter(isVisibleMenuItem)
                .filter((homeItem) => !skipHomeItems.includes(normalizeMenuName(homeItem.name)))
                .forEach((homeItem) => blocks.push({ kind: 'home-item', item: homeItem }));
            continue;
        }

        if (item.menu && item.menu.length > 0) {
            blocks.push({
                kind: 'category',
                key: getCategoryKey(item, index),
                openKey: getCategoryOpenKey(item),
                label: item.name,
                icon: item.icon,
                items: item.menu.filter(isVisibleMenuItem),
                headerItem: hasMenuUrl(item) ? item : undefined
            });
            continue;
        }

        if (hasMenuUrl(item)) {
            blocks.push({ kind: 'home-item', item });
        }
    }

    return blocks;
};

// Resolves the user's starred keys against the live menu tree, in the saved order.
const resolveStarredItems = (allItems: ItemMenu[], starredKeys: string[]): { item: ItemMenu; starredKey: string }[] => {
    const seen = new Set<string>();
    return starredKeys
        .map((key) => ({ starredKey: key, item: allItems.find((item) => isStarredKeyMatch(item, key)) }))
        .filter((entry): entry is { item: ItemMenu; starredKey: string } => {
            const item = entry.item;
            if (!item || !isStarrableItem(item)) return false;
            const id = item.key || item.urlPage;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
};

const mergeVisibleStarredOrder = (starredKeys: string[], visibleKeys: string[], nextVisibleKeys: string[]) => {
    const visibleKeySet = new Set(visibleKeys);
    const nextVisibleQueue = [...nextVisibleKeys];

    return starredKeys.map((key) => (visibleKeySet.has(key) ? nextVisibleQueue.shift()! : key));
};

const StarredSortableMenuItem: FunctionComponent<{
    item: ItemMenu;
    starredKey: string;
    bestSelected: ItemMenu | null;
    goToMenu: (item: ItemMenu) => void;
    disabled: boolean;
    canReorder: boolean;
}> = ({ item, starredKey, bestSelected, goToMenu, disabled, canReorder }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: starredKey,
        disabled: !canReorder
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <StarredSortableItemContainer ref={setNodeRef} style={style} $isDragging={isDragging}>
            <SidebarMenuItem
                itemMenu={item}
                isHeader={false}
                goToMenu={goToMenu}
                disabled={disabled}
                bestSelected={bestSelected}
                isProminent
                canStar
                canReorder={canReorder}
                starredKey={starredKey}
                dragAttributes={canReorder ? attributes : undefined}
                dragListeners={canReorder ? listeners : undefined}
                setDragHandleRef={canReorder ? setActivatorNodeRef : undefined}
            />
        </StarredSortableItemContainer>
    );
};

// Dynamic starred pages, rendered right after Orders as a plain continuation
// of the menu list (no header). The items come from the user's starred keys
// resolved against the live menu tree, so the user controls exactly which
// pages are always reachable here.
const StarredSection: FunctionComponent<{
    allItems: ItemMenu[];
    bestSelected: ItemMenu | null;
    goToMenu: (item: ItemMenu) => void;
    iamUser: boolean;
    iamUserPermissions: string[];
}> = ({ allItems, bestSelected, goToMenu, iamUser, iamUserPermissions }) => {
    const { starredKeys, saveStarredKeysOrder } = useStarredMenu();
    const listRef = useRef<HTMLDivElement | null>(null);
    const starredItems = useMemo(() => resolveStarredItems(allItems, starredKeys), [allItems, starredKeys.join(' ')]);
    const visibleStarredKeys = useMemo(() => starredItems.map((entry) => entry.starredKey), [starredItems]);
    const canReorderStarredItems = visibleStarredKeys.length > 1;

    const handleDragStart = () => {
        window.scrollTo(0, 0);
        document.body.style.overflow = 'hidden';
    };

    const handleDragCancel = () => {
        document.body.style.overflow = '';
    };

    const handleDragEnd = (event: DragEndEvent) => {
        document.body.style.overflow = '';

        const activeId = event.active.id.toString();
        const oldIndex = visibleStarredKeys.indexOf(activeId);
        if (oldIndex < 0) return;

        let newIndex = event.over ? visibleStarredKeys.indexOf(event.over.id.toString()) : -1;

        if (newIndex < 0) {
            const activeRect = event.active.rect.current.translated ?? event.active.rect.current.initial;
            const listRect = listRef.current?.getBoundingClientRect();

            if (activeRect && listRect) {
                const activeCenterY = activeRect.top + activeRect.height / 2;
                if (activeCenterY < listRect.top) newIndex = 0;
                else if (activeCenterY > listRect.bottom) newIndex = visibleStarredKeys.length - 1;
            }
        }

        if (newIndex < 0 || oldIndex === newIndex) return;

        const nextVisibleKeys = arrayMove(visibleStarredKeys, oldIndex, newIndex);
        saveStarredKeysOrder(mergeVisibleStarredOrder(starredKeys, visibleStarredKeys, nextVisibleKeys));
    };

    if (starredItems.length === 0) return null;

    return (
        <DndContext
            autoScroll={{
                activator: AutoScrollActivator.Pointer,
                order: TraversalOrder.ReversedTreeOrder
            }}
            modifiers={[restrictToFirstScrollableAncestor, restrictToVerticalAxis]}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <SortableContext strategy={verticalListSortingStrategy} items={visibleStarredKeys}>
                <div ref={listRef}>
                    {starredItems.map(({ item, starredKey }) => (
                        <StarredSortableMenuItem
                            key={starredKey}
                            item={item}
                            starredKey={starredKey}
                            bestSelected={bestSelected}
                            goToMenu={goToMenu}
                            disabled={!isAllowed(iamUserPermissions, iamUser, item.permissions)}
                            canReorder={canReorderStarredItems}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

const SectionedMenu: FunctionComponent<{
    root: ItemMenu;
    goToMenu: (item: ItemMenu) => void;
    iamUser: boolean;
    iamUserPermissions: string[];
    expandedCategoryKey?: string | null;
    onExpandedCategoryOpened?: () => void;
    initialOpenCategories?: Record<string, boolean>;
    onOpenCategoriesChange?: (openCategories: Record<string, boolean>) => void;
}> = ({ root, goToMenu, iamUser, iamUserPermissions, expandedCategoryKey, onExpandedCategoryOpened, initialOpenCategories, onOpenCategoriesChange }) => {
    // Subscribe to URL changes so `isMenuItemSelected` (which reads
    // window.location.href) is re-evaluated on every navigation.
    const location = useLocation();
    const blocks = useMemo<MenuBlock[]>(() => buildCategorizedMenu(root), [root]);
    const allItems = useMemo(() => flattenMenuItems(root), [root]);
    const bestSelected = useMemo(() => findBestSelectedItem(root), [root, location.pathname, location.search]);

    // The starred section is injected right after the Orders category and the top-level
    // entries that follow it (e.g. Pre-Set Configurations in the configurator menu). If Orders
    // is unavailable, fall back to just before the first collapsible category.
    const ordersIndex = blocks.findIndex((block) => block.kind === 'category' && block.key === 'orders');
    const firstCategoryIndex = blocks.findIndex((block) => block.kind === 'category');
    let starredAfterIndex = ordersIndex >= 0 ? ordersIndex : firstCategoryIndex >= 0 ? firstCategoryIndex - 1 : blocks.length - 1;
    if (ordersIndex >= 0) {
        while (blocks[starredAfterIndex + 1]?.kind === 'home-item') starredAfterIndex++;
    }
    const firstCategoryAfterStarredIndex = blocks.findIndex((block, index) => block.kind === 'category' && index > starredAfterIndex);

    const getInitialOpenCategory = () => {
        const state: Record<string, boolean> = {};
        for (const block of blocks) {
            if (block.kind === 'category') {
                state[block.key] = initialOpenCategories?.[block.key] ?? false;
            }
        }
        return state;
    };

    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(getInitialOpenCategory);

    useEffect(() => {
        if (!expandedCategoryKey) return;

        const blockToOpen = blocks.find(
            (block): block is Extract<MenuBlock, { kind: 'category' }> => block.kind === 'category' && block.openKey === expandedCategoryKey
        );

        if (!blockToOpen) {
            onExpandedCategoryOpened?.();
            return;
        }

        setOpenCategories((prev) => {
            const next: Record<string, boolean> = {};
            for (const k of Object.keys(prev)) {
                next[k] = k === blockToOpen.key;
            }
            onOpenCategoriesChange?.(next);
            return next;
        });
        onExpandedCategoryOpened?.();
    }, [expandedCategoryKey]);

    const [isDirty, incrementStoreKey] = useRelayStore((state) => [state.isRelayStoreDirty, state.incrementRelayStoreKey]);
    const navigate = useNavigate();
    const { startPageTransition } = usePageTransition();

    const toggle = (key: string) => {
        setOpenCategories((prev) => {
            const isOpening = !prev[key];
            if (!isOpening) {
                // Already open: clicking the same parent again collapses it.
                const next = { ...prev, [key]: false };
                onOpenCategoriesChange?.(next);
                return next;
            }
            // Close all others when opening one
            const next: Record<string, boolean> = {};
            for (const k of Object.keys(prev)) {
                next[k] = k === key;
            }
            onOpenCategoriesChange?.(next);
            return next;
        });
    };

    const handleHeaderClick = (key: string, headerItem?: ItemMenu) => {
        toggle(key);
        if (headerItem) {
            if (isDirty) incrementStoreKey();
            const pathname = getPathName(headerItem.urlPage);
            if (window.location.href.toLowerCase().includes('dam')) window.location.href = pathname;
            else {
                startPageTransition(() => {
                    navigate(pathname);
                });
            }
        }
    };

    const renderItem = (
        item: ItemMenu,
        key: string | number,
        opts?: { hideIcon?: boolean; forceNavigate?: boolean; isProminent?: boolean; canStar?: boolean }
    ) => (
        <div key={key}>
            <SidebarMenuItem
                itemMenu={item}
                isHeader={false}
                goToMenu={goToMenu}
                disabled={!isAllowed(iamUserPermissions, iamUser, item.permissions)}
                bestSelected={bestSelected}
                hideIcon={opts?.hideIcon}
                forceNavigate={opts?.forceNavigate}
                isProminent={opts?.isProminent}
                canStar={opts?.canStar}
            />
        </div>
    );

    const renderBlock = (block: MenuBlock, i: number) => {
        if (block.kind === 'home-item') {
            return renderItem(block.item, `home-item-link-${i}`, { isProminent: true });
        }

        const isOpen = !!openCategories[block.key];
        const CategoryIcon = block.icon ? GetIcon(block.icon) : undefined;
        const headerIsSelected =
            isMenuKeySelected(block.key) ||
            isMenuKeySelected(block.openKey) ||
            (block.headerItem ? isMenuItemExactSelected(block.headerItem, bestSelected) : false);
        // A nested section without its own page (e.g. Libraries inside Design Tools) is a sub-category:
        // render its label as a static header followed by its items. Nested sections with a page keep
        // rendering as a single link (e.g. AI Creative Tools).
        const renderCategoryItems = () =>
            block.items.map((item, j) => {
                const subItems = item.menu.filter(isVisibleMenuItem);
                if (subItems.length === 0 || hasMenuUrl(item)) {
                    return renderItem(item, `${block.key}-${j}`, { hideIcon: true, forceNavigate: true, canStar: true });
                }

                const SubCategoryIcon = item.icon ? GetIcon(item.icon) : undefined;
                return (
                    <div key={`${block.key}-${j}`}>
                        <MenuSectionHeader $isStatic $isUppercase>
                            {SubCategoryIcon && (
                                <MenuSectionIcon aria-hidden='true'>
                                    <SubCategoryIcon />
                                </MenuSectionIcon>
                            )}
                            <span>{item.name}</span>
                        </MenuSectionHeader>
                        <SubCategoryItems>
                            {subItems.map((subItem, k) =>
                                renderItem(subItem, `${block.key}-${j}-${k}`, { hideIcon: true, forceNavigate: true, canStar: true })
                            )}
                        </SubCategoryItems>
                    </div>
                );
            });

        return (
            <>
                <MenuSectionHeader
                    onClick={() => handleHeaderClick(block.key, block.headerItem)}
                    $isSelected={headerIsSelected}
                    $isUppercase={i > starredAfterIndex}
                >
                    {CategoryIcon && (
                        <MenuSectionIcon aria-hidden='true'>
                            <CategoryIcon />
                        </MenuSectionIcon>
                    )}
                    <span>{toTitleCase(block.label)}</span>
                    <MenuSectionArrow $isOpen={isOpen}>&#9654;</MenuSectionArrow>
                </MenuSectionHeader>
                <MenuSectionBody $collapsed={!isOpen}>{renderCategoryItems()}</MenuSectionBody>
            </>
        );
    };

    return (
        <>
            {blocks.map((block, i) => (
                <React.Fragment key={block.kind === 'category' ? block.key : `${block.kind}-${i}`}>
                    {block.kind === 'category' && i === firstCategoryAfterStarredIndex && <MenuDivider />}
                    {renderBlock(block, i)}
                    {i === starredAfterIndex && (
                        <StarredSection
                            allItems={allItems}
                            bestSelected={bestSelected}
                            goToMenu={goToMenu}
                            iamUser={iamUser}
                            iamUserPermissions={iamUserPermissions}
                        />
                    )}
                </React.Fragment>
            ))}
        </>
    );
};

const SidebarMenuItems: FunctionComponent<SidebarMenuItemsProps> = (props) => {
    const location = useLocation();
    const isHome = props.currentMenu.name === 'HOME';
    const bestSelected = useMemo(() => findBestSelectedItem(props.currentMenu, isSubMenuItemSelected), [props.currentMenu, location.pathname, location.search]);

    if (isHome) {
        return (
            <NavbarContainer>
                <Navbar style={{ position: 'relative' }}>
                    <SectionedMenu
                        root={props.currentMenu}
                        goToMenu={props.goToMenu}
                        iamUser={props.iamUser}
                        iamUserPermissions={props.iamUserPermissions}
                        expandedCategoryKey={props.expandedCategoryKey}
                        onExpandedCategoryOpened={props.onExpandedCategoryOpened}
                        initialOpenCategories={props.initialOpenCategories}
                        onOpenCategoriesChange={props.onOpenCategoriesChange}
                    />
                </Navbar>
            </NavbarContainer>
        );
    }

    return (
        <NavbarContainer>
            <Navbar>{SidebarSubmenuList(props.currentMenu, props.goToMenu, props.iamUser, props.iamUserPermissions, bestSelected)}</Navbar>
        </NavbarContainer>
    );
};

export default SidebarMenuItems;
